import { defineComponent, h, ref, onBeforeUnmount } from "vue";
import type { QueryProgress } from "./task-progress.js";
import {
  evidenceProgressFile,
  type EvidenceProgressState,
} from "@knowyet/sdk/evidence";
export function phase(stage: string) {
  if (stage === "extraction.omitted") return "抽取诊断";
  if (stage === "build.accepted") return "构建完成";
  if (stage === "chunks") return "整理原文片段";
  if (stage === "source.acceptance") return "溯源检查";
  if (stage === "ingest") return "接收材料";
  if (stage.startsWith("document")) return "识别与转换";
  if (stage.startsWith("directory")) return "组织目录";
  if (stage.startsWith("units")) return "信息单元";
  if (stage.startsWith("open") || stage.startsWith("mapping"))
    return "抽取与映射";
  if (stage.startsWith("schema")) return "结构定义";
  if (stage.startsWith("graph")) return "构建图";
  if (stage.startsWith("publish") || stage.startsWith("embedding"))
    return "索引与发布";
  if (stage.startsWith("hypothesis") || stage.startsWith("investigate"))
    return "检索与分析";
  if (stage === "provider") return "模型接口";
  if (stage.startsWith("candidate")) return "校验与修正";
  return stage;
}
const elapsed = (start?: string, end?: string) => {
  if (!start || !end) return "";
  const sec = Math.max(
    0,
    Math.round((Date.parse(end) - Date.parse(start)) / 1000),
  );
  return sec >= 60 ? `${Math.floor(sec / 60)}分${sec % 60}秒` : `${sec}秒`;
};
export const QueryProgressPanel = defineComponent({
  props: { state: { type: Object as () => QueryProgress, required: true }, connected: Boolean },
  setup(props) {
    const now = ref(Date.now());
    const timer = setInterval(() => { if (!props.state.terminal) now.value = Date.now(); }, 1000);
    onBeforeUnmount(() => clearInterval(timer));
    return () => h("div", { class: "build-progress", "data-testid": "query-progress", role: "status" }, [
      h("strong", props.state.terminal === "completed" ? "答案与引用已生成" : props.state.terminal ? "分析已中断，可恢复" : props.state.activity),
      h("p", { class: "muted" }, [
        `已执行 ${props.state.calls} 次检索、读取或计算 · 耗时 `,
        elapsed(props.state.startedAt, props.state.terminal ? props.state.updatedAt : new Date(now.value).toISOString()) || "—",
        ` · ${props.connected ? "进度已连接" : props.state.terminal ? "执行已结束" : "正在连接进度"}`,
        props.state.updatedAt ? ` · 最近活动 ${new Date(props.state.updatedAt).toLocaleTimeString()}` : "",
      ].join("")),
    ]);
  },
});
export const ProgressPanel = defineComponent({
  props: {
    state: { type: Object as () => EvidenceProgressState, required: true },
    connected: Boolean,
  },
  setup(props) {
    const now = ref(Date.now());
    const timer = setInterval(() => {
      if (!props.state.terminal) now.value = Date.now();
    }, 1000);
    onBeforeUnmount(() => clearInterval(timer));
    return () => {
      const s = props.state,
        files = Object.values(s.files),
        steps = Object.values(s.steps),
        phases = [...new Set(steps.map((x) => phase(x.stage)))],
        known = Object.keys(s.aliases).length > 0;
      return h(
        "div",
        { class: "build-progress", "data-testid": "build-progress" },
        [
          h("div", { class: "progress-metrics" }, [
            h("div", [
              h(
                "strong",
                `${known ? files.filter((f) => f.converted).length : "—"} / ${files.length}`,
              ),
              h("span", "文件转换完成"),
            ]),
            h("div", [
              h(
                "strong",
                `${known ? files.filter((f) => f.extracted).length : "—"} / ${files.length}`,
              ),
              h("span", "文件抽取完成"),
            ]),
            h("div", [
              h(
                "strong",
                elapsed(
                  s.startedAt,
                  s.terminal ? s.updatedAt : new Date(now.value).toISOString(),
                ) || "—",
              ),
              h("span", "本次任务耗时"),
            ]),
          ]),
          h(
            "p",
            { class: "muted", role: "status" },
            `${s.terminal === "completed" ? "构建已完成" : s.terminal ? "任务已结束" : props.connected ? "事件流已连接" : "正在连接进度"}${s.updatedAt ? " · 最近活动 " + new Date(s.updatedAt).toLocaleTimeString() : ""} · 文件抽取完成后仍需完成全局结构整理与图发布。`,
          ),
          !known
            ? h(
                "p",
                { class: "muted" },
                "此事件记录暂未提供文件级状态，计数显示为未知；步骤明细仍可查看。",
              )
            : null,
          h(
            "div",
            { class: "phase-grid" },
            phases.map((p) => {
              const group = steps.filter((x) => phase(x.stage) === p),
                done = group.filter((x) => x.state === "completed").length;
              return h("div", { class: "phase" }, [
                h("strong", p),
                h(
                  "span",
                  `${done} 个步骤完成 · ${s.terminal ? 0 : group.filter((x) => x.state === "running").length} 个进行中${group.some((x) => x.state === "failed") ? " · 有中断" : ""}`,
                ),
              ]);
            }),
          ),
          h("table", { class: "file-progress" }, [
            h("thead", [
              h(
                "tr",
                ["文件", "转换", "抽取", "当前处理"].map((x) => h("th", x)),
              ),
            ]),
            h(
              "tbody",
              files.map((f) => {
                const failed = steps.filter(x => x.state === "failed" && evidenceProgressFile(s, x.source) === f.file);
                const conversionFailed = failed.some(x => x.stage.startsWith("document"));
                const extractionFailed = failed.some(x => /^(directory|units|open|mapping|graph)/.test(x.stage));
                const active = steps.filter(
                  (x) =>
                    x.state === "running" &&
                    evidenceProgressFile(s, x.source) === f.file,
                );
                return h("tr", [
                  h("td", f.file),
                  h(
                    "td",
                    f.converted
                      ? `已完成${f.pages ? " · " + f.pages + "页" : ""}`
                      : conversionFailed
                        ? "已中断，可恢复"
                      : known
                        ? "待完成"
                        : "未知",
                  ),
                  h(
                    "td",
                    f.extracted
                      ? `已完成${f.units ? " · " + f.units + "单元" : ""}`
                      : extractionFailed
                        ? "已中断，可恢复"
                      : known
                        ? "待完成"
                        : "未知",
                  ),
                  h(
                    "td",
                    f.issue ? (f.issue.status==='blocked'?'文件不可用，其他材料可继续':'原文可检索，抽取待处理') : s.terminal
                      ? s.terminal === "completed"
                        ? "已发布"
                        : "已保存，可恢复"
                      : !active.length && failed.length && !f.extracted
                        ? "部分步骤未通过，结果已保存"
                      : [
                          ...new Set(
                            active.map(
                              (x) =>
                                phase(x.stage) +
                                (x.page ? " · 第" + x.page + "页" : ""),
                            ),
                          ),
                        ].join("、") || "等待下一阶段",
                  ),
                ]);
              }),
            ),
          ]),
          h("details", [
            h("summary", `步骤明细（${steps.length}）`),
            h(
              "ol",
              { class: "progress" },
              steps
                .slice(-200)
                .reverse()
                .map((x) =>
                  h("li", [
                    `${phase(x.stage)} · ${evidenceProgressFile(s, x.source) ?? (x.source.length > 30 ? "全局任务" : x.source)} · ${x.state === "completed" ? "完成" : x.state === "failed" ? "中断" : s.terminal ? "本次已结束" : "进行中"}${x.restored ? "（恢复已保存结果）" : ""}`,
                    x.total !== undefined
                      ? ` · ${x.completed ?? 0}/${x.total} ${x.unit ?? ""}`
                      : "",
                    x.page ? ` · 第 ${x.page} 页` : "",
                    x.detail ? " · " + x.detail : "",
                    x.startedAt
                      ? " · " +
                        elapsed(
                          x.startedAt,
                          x.state === "running" && !s.terminal
                            ? new Date(now.value).toISOString()
                            : x.updatedAt,
                        )
                      : "",
                  ]),
                ),
            ),
          ]),
        ],
      );
    };
  },
});
